import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useUpdateBuyOrder } from 'services/buy-process';
import { RadioConvertUtils } from '../../../../utils/radio/radio';
import { RootState } from '../../../../global';
import { useBuyProcessForm } from '../../buy-process-form-utils';
import { SignAuthority } from '../../../../global/entities/BuyOrder';
import { saveEditRepresentatives } from '../../../admin/buy-process/buy-order-edit/buy-order-edit-redux';
import {
  onGoBackInForm,
  onGoForwardInForm,
  useSelectPeople,
} from '../../use-buy-process-form';
import { useIsAdminOnBuyOrderEditForm } from '../../../../utils/buy-order/use-is-admin-on-buy-order-edit-form';
import {
  selectBuyCeoPersonFactory,
  selectBuyRepresentativesPeopleFactory,
} from '../../../../services/buy-process/selectors';
import { RepresentativesFormState } from './representatives-types';
import {
  representativeObjectHasValues,
  representativesValidationSchema,
} from './representatives-utils';

function filterRepresentativesWithValues(
  data: RepresentativesFormState,
): RepresentativesFormState {
  const representatives = data.representatives?.filter((representative) =>
    representativeObjectHasValues(representative));

  return {
    ...data,
    representatives,
  };
}

export const useRepresentatives = () => {
  const representativesFieldArrayName = 'representatives';

  const dispatch = useDispatch();
  const { setRequestedStep, id, goToRequestedStep } = useBuyProcessForm();
  const { signAuthority, officialCeo, signAuthorityUserDefined } = useSelector(
    (state: RootState) => state.buyOrderEdit[id].representatives,
  );

  const representativesPeople = useSelector(
    selectBuyRepresentativesPeopleFactory(id),
  );
  const ceo = useSelector(selectBuyCeoPersonFactory(id));
  const people = useSelectPeople(id);

  const formMethods = useForm<RepresentativesFormState>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(representativesValidationSchema),
    defaultValues: {
      ceo: ceo || {},
      representatives:
        representativesPeople.length === 0 ? [{}, {}] : representativesPeople,
      officialCeo: RadioConvertUtils.boolToStr(officialCeo),
      signAuthority: signAuthority || SignAuthority.AB1,
      signAuthorityUserDefined,
    },
  });

  const { getValues, trigger } = formMethods;

  useEffect(() => {
    goToRequestedStep(trigger, () => {
      if (
        getValues().officialCeo === undefined
        || getValues().ceo === undefined
      ) {
        return;
      }

      dispatch(
        saveEditRepresentatives({
          id,
          ...filterRepresentativesWithValues(getValues()),
        }),
      );
    });
  }, [dispatch, getValues, goToRequestedStep, id, officialCeo, trigger]);

  const representativesFieldArray = useFieldArray({
    name: representativesFieldArrayName,
    control: formMethods.control,
  });

  const isAdmin = useIsAdminOnBuyOrderEditForm();
  const [updateBuyOrder, updateBuyOrderMeta] = useUpdateBuyOrder(id);

  return {
    formMethods,
    representativesFieldArray,
    representativesFieldArrayName,
    people,
    isUpdating: updateBuyOrderMeta.isLoading,
    onSubmit: formMethods.handleSubmit((data) => {
      dispatch(saveEditRepresentatives({ ...data, id }));
      updateBuyOrder();

      if (!isAdmin) {
        setRequestedStep((s) => s + 1);
      }
    }),
    onForward: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoForwardInForm(setRequestedStep, e),
    onBack: (e: React.MouseEvent<HTMLButtonElement>) =>
      onGoBackInForm(setRequestedStep, formMethods.trigger, e),
  };
};
